import { forceAddonsMinMaxConstraints } from '@proton/components/containers/payments/planCustomizer';
import { getAddonLimit } from '@proton/payments/core/addon/addons';
import { type ADDON_NAMES, ADDON_PREFIXES, CURRENCIES, PLANS } from '@proton/payments/core/constants';
import type { Currency } from '@proton/payments/core/interface';
import { getSupportedAddons, isAddonType } from '@proton/payments/core/plan/addons';
import { getPlanMaxIPs } from '@proton/payments/core/plan/feature-limits';
import { correctDeprecatedPlanName } from '@proton/payments/core/plan/helpers';
import type { Plan, PlansMap } from '@proton/payments/core/plan/interface';
import { FREE_PLAN } from '@proton/payments/core/subscription/freePlans';
import { getValidCycle } from '@proton/payments/core/subscription/helpers';
import { getPlanByName, planToPlanIDs } from '@proton/payments/core/subscription/plans-map-wrapper';
import type { OtherProductParam, ProductParam } from '@proton/shared/lib/apps/product';
import { otherProductParamValues } from '@proton/shared/lib/apps/product';
import type { APP_NAMES } from '@proton/shared/lib/constants';
import { APPS } from '@proton/shared/lib/constants';
import { getCookie } from '@proton/shared/lib/helpers/cookies';
import clamp from '@proton/utils/clamp';

import type { PlanParameters, SignupDefaults } from '../single-signup-v2/interface';

export const SERVICES: { [key: string]: APP_NAMES } = {
    mail: APPS.PROTONMAIL,
    calendar: APPS.PROTONCALENDAR,
    drive: APPS.PROTONDRIVE,
    vpn: APPS.PROTONVPN_SETTINGS,
    pass: APPS.PROTONPASS,
    docs: APPS.PROTONDOCS,
    wallet: APPS.PROTONWALLET,
    lumo: APPS.PROTONLUMO,
    authenticator: APPS.PROTONAUTHENTICATOR,
    meet: APPS.PROTONMEET,
};

const getProduct = (maybeProduct: string | undefined): APP_NAMES | undefined => {
    if (!maybeProduct) {
        return;
    }
    const directMatch = SERVICES[maybeProduct];
    if (directMatch) {
        return directMatch;
    }
    const partialMatch = Object.keys(SERVICES).find((service) => maybeProduct.startsWith(service));
    if (partialMatch) {
        return SERVICES[partialMatch];
    }
};

const productParams = new Set(otherProductParamValues);

const getSanitisedProductParam = (value: string | undefined): OtherProductParam | undefined => {
    if (productParams.has(value as any)) {
        return value as OtherProductParam;
    }
};

const getProductParam = (
    product: APP_NAMES | undefined,
    productParam: string | undefined
): ProductParam | undefined => {
    const sanitisedProductParam = getSanitisedProductParam(productParam);
    return product || sanitisedProductParam;
};

export interface ProductParams {
    product: APP_NAMES | undefined;
    productParam: ProductParam;
}

export const getProductParams = (pathname: string, searchParams: URLSearchParams): ProductParams => {
    const maybeProductPathnames = pathname.split('/').filter(Boolean);
    const maybeProductParam = (
        searchParams.get('service') ||
        searchParams.get('product') ||
        searchParams.get('app') ||
        undefined
    )
        ?.toLowerCase()
        ?.replace('proton-', '');

    const maybeProducts = [...maybeProductPathnames, maybeProductParam];
    let [product] = maybeProducts.map((value) => getProduct(value)).filter(Boolean);
    let [productParam] = maybeProducts.map((value) => getProductParam(product, value)).filter(Boolean);

    productParam = productParam || 'generic';
    const planParam = searchParams.get('plan') as any;

    if (productParam === 'business') {
        if ([PLANS.MAIL_PRO, PLANS.MAIL_BUSINESS].includes(planParam)) {
            product = APPS.PROTONMAIL;
            productParam = APPS.PROTONMAIL;
        }
    }

    if (!product) {
        const plans = [PLANS.MAIL_PRO, PLANS.MAIL_BUSINESS];
        if (plans.includes(planParam)) {
            product = APPS.PROTONMAIL;
        }
    }

    return { product, productParam };
};

export const getSignupSearchParams = (
    pathname: string,
    searchParams: URLSearchParams,
    defaults?: Partial<SignupDefaults>
) => {
    const maybeCurrency = searchParams.get('currency')?.toUpperCase() as Currency | undefined;
    const currency = maybeCurrency && CURRENCIES.includes(maybeCurrency) ? maybeCurrency : undefined;

    const maybeCycle = Number(searchParams.get('billing')) || Number(searchParams.get('cycle'));
    const cycle = getValidCycle(maybeCycle);

    const maybeMinimumCycle = Number(searchParams.get('minimumCycle'));
    const minimumCycle = getValidCycle(maybeMinimumCycle);

    const maybeUsers = Number(searchParams.get('users'));
    const users = maybeUsers >= 1 && maybeUsers <= getAddonLimit(ADDON_PREFIXES.MEMBER) ? maybeUsers : undefined;
    const maybeDomains = Number(searchParams.get('domains'));
    const domains =
        maybeDomains >= 1 && maybeDomains <= getAddonLimit(ADDON_PREFIXES.DOMAIN) ? maybeDomains : undefined;
    const maybeIps = Number(searchParams.get('ips'));
    const ips = maybeIps >= 1 && maybeIps <= getAddonLimit(ADDON_PREFIXES.IP) ? maybeIps : undefined;

    const { product } = getProductParams(pathname, searchParams);

    // plan is validated by comparing plans after it's loaded
    const maybePreSelectedPlan = searchParams.get('plan');
    const maybeDefaultPlan = searchParams.get('defaultplan');

    const referrer = searchParams.get('referrer') || undefined; // referral ID
    const invite = searchParams.get('invite') || undefined;
    const coupon = searchParams.get('coupon')?.toUpperCase() || undefined;
    const type = searchParams.get('type') || undefined;
    const maybeHfp = searchParams.get('hfp');
    const hideFreePlan = ['true', '1'].includes(maybeHfp as any) || getCookie('offer') === 'bestdeal' || false;
    const email = searchParams.get('email') || undefined;
    const orgName = searchParams.get('orgName') || undefined;
    const source = searchParams.get('source') || undefined;
    const noPromo = searchParams.get('noPromo');
    const trial = searchParams.get('trial') === 'true';
    const cardless = searchParams.get('cardless') === 'true';

    const firstName = searchParams.get('firstName') || undefined;
    const lastName = searchParams.get('lastName') || undefined;
    const vatNumber = searchParams.get('vatNumber') || undefined;
    const city = searchParams.get('city') || undefined;
    const streetAddress = searchParams.get('streetAddress') || undefined;

    const visitorId = searchParams.get('visitorId') || undefined;

    // vpn2024SignupExperiment
    const vpn2024SignupExperimentVariant = searchParams.get('v') || undefined;

    return {
        email,
        coupon,
        currency,
        cycle: cycle || defaults?.cycle,
        minimumCycle,
        preSelectedPlan: correctDeprecatedPlanName(maybePreSelectedPlan || defaults?.plan),
        defaultPlan: correctDeprecatedPlanName(maybeDefaultPlan || defaults?.plan),
        product,
        users,
        noPromo: noPromo !== null && noPromo !== 'false' && noPromo !== '0',
        domains,
        ips,
        referrer,
        invite,
        type,
        hideFreePlan,
        orgName,
        source,
        trial,
        cardless,
        firstName,
        lastName,
        vatNumber,
        city,
        streetAddress,
        visitorId,
        vpn2024SignupExperimentVariant,
    };
};

export const getPlanIDsFromParams = (
    plans: Plan[],
    currency: Currency,
    signupParameters: {
        preSelectedPlan?: string;
        domains?: number;
        ips?: number;
        users?: number;
    },
    defaults: Pick<SignupDefaults, 'plan' | 'addons'>
): PlanParameters => {
    const freePlanIDs = {};

    const defaultPlan =
        defaults.plan === FREE_PLAN.Name
            ? FREE_PLAN
            : (getPlanByName(plans, defaults.plan, currency, undefined, false) ?? FREE_PLAN);
    const defaultPlanIDs = { ...planToPlanIDs(defaultPlan), ...defaults.addons };

    const defaultResponse = {
        planIDs: defaultPlanIDs,
        plan: defaultPlan,
        defined: false,
    };

    if (!signupParameters.preSelectedPlan) {
        return defaultResponse;
    }

    if (signupParameters.preSelectedPlan === 'free') {
        return { planIDs: freePlanIDs, defined: true, plan: FREE_PLAN };
    }

    const plan = getPlanByName(plans, signupParameters.preSelectedPlan, currency, undefined, false);

    if (!plan) {
        return defaultResponse;
    }

    const preSelectedPlanIDs = { [plan.Name]: 1 };
    const planIDs =
        // if the default plan has addons and if user selected the default plan via URL parameters, then we want to
        // automatically transfer the default addons if there are any
        plan.Name === defaultPlan.Name
            ? {
                  ...preSelectedPlanIDs,
                  ...defaults.addons,
              }
            : preSelectedPlanIDs;

    const supportedAddons = getSupportedAddons(planIDs);

    if (signupParameters.users !== undefined) {
        const usersAddon = plans.find(
            ({ Name }) =>
                isAddonType(Name, ADDON_PREFIXES.MEMBER) && supportedAddons[Name as keyof typeof supportedAddons]
        );

        const clampedUsers = clamp(
            signupParameters.users,
            0,
            usersAddon ? getAddonLimit(usersAddon.Name as ADDON_NAMES) : 0
        );
        const amount = clampedUsers - plan.MaxMembers;
        if (usersAddon && amount > 0) {
            planIDs[usersAddon.Name] = amount;
        }
    }

    if (signupParameters.domains !== undefined) {
        const domainsAddon = plans.find(
            ({ Name }) =>
                isAddonType(Name, ADDON_PREFIXES.DOMAIN) && supportedAddons[Name as keyof typeof supportedAddons]
        );
        const amount = signupParameters.domains - plan.MaxDomains;
        if (domainsAddon && amount > 0) {
            planIDs[domainsAddon.Name] = amount;
        }
    }

    if (signupParameters.ips !== undefined) {
        const ipsAddon = plans.find(
            ({ Name }) => isAddonType(Name, ADDON_PREFIXES.IP) && supportedAddons[Name as keyof typeof supportedAddons]
        );
        const amount = signupParameters.ips - (getPlanMaxIPs(plan) + (ipsAddon?.Quantity || 0));
        if (ipsAddon && amount > 0) {
            planIDs[ipsAddon.Name] = amount;
        }
    }

    return { planIDs, defined: true, plan };
};

export const getPlanIDsFromParamsWithForcedAddons = ({
    plans,
    currency,
    signupParameters,
    defaults,
    plansMap,
}: {
    plans: Plan[];
    currency: Currency;
    signupParameters: {
        preSelectedPlan?: string;
        domains?: number;
        ips?: number;
        users?: number;
    };
    defaults: Pick<SignupDefaults, 'plan' | 'addons'>;
    plansMap: PlansMap;
}) => {
    const result = getPlanIDsFromParams(plans, currency, signupParameters, defaults);
    const planIDs =
        forceAddonsMinMaxConstraints({
            selectedPlanIDs: result.planIDs,
            plansMap,
            currency,
            subscription: undefined,
        }) ?? result.planIDs;
    return { ...result, planIDs };
};
